import { useState, useEffect, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Heart, Brain, Users, Sparkles, Play, Award, ArrowRight, Leaf, Menu, X, PenLine, Clock, Shield, Calendar, Video, MessageCircle, Headphones, ChevronDown, Lock, Crown, Check } from "lucide-react";

export default function LandingPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);

  const featureMenuItems = [
    { label: "Dashboard", path: "/feature-access?feature=Dashboard", free: true },
    { label: "Mind-SPA", path: "/feature-access?feature=Mind-SPA", free: true },
    { label: "AI Coach", path: "/feature-access?feature=AI Coach", free: true },
    { label: "Mood Tracker", path: "/feature-access?feature=Mood Tracker", free: true },
    { label: "SMAHOO Coins", path: "/feature-access?feature=SMAHOO Coins", free: true },
    { label: "Community", path: "/feature-access?feature=Community", free: true },
    { label: "Sessions & Bookings", path: "/feature-access?feature=Sessions %26 Bookings", free: true },
    { label: "Wellness Calendar", path: "/feature-access?feature=Wellness Calendar", free: true },
    { label: "Blog", path: "/blog", free: true },
    { label: "ME.WELL Digest", path: "/feature-access?feature=ME.WELL Digest", free: true },
    { label: "SMAHOO ChatBOTs", path: "/feature-access?feature=SMAHOO ChatBOTs", premium: true },
    { label: "Streaming", path: "/feature-access?feature=Streaming", premium: true },
    { label: "OnCALL+ Live ICU", path: "/feature-access?feature=OnCALL%2B Live ICU", premium: true },
    { label: "Life Skills", path: "/feature-access?feature=Life Skills", premium: true },
    { label: "Gift Basket Store", path: "/feature-access?feature=Gift Basket Store", premium: true },
    { label: "Mind-SPA Retreat", path: "/feature-access?feature=Mind-SPA Retreat", premium: true },
    { label: "Wellness Mall", path: "/feature-access?feature=Wellness Mall", premium: true },
  ];
  const [showNudge, setShowNudge] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  const [needs2FA, setNeeds2FA] = useState(false);
  const [pre2faToken, setPre2faToken] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [authTab, setAuthTab] = useState("login");
  const { login, verify2FA, register, user } = useAuth();

  useEffect(() => {
    fetch(`${API}/blog?limit=3`)
      .then(r => r.json())
      .then(data => { if (data.posts) setBlogPosts(data.posts.slice(0, 3)); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (nudgeDismissed) return;
      const scrollPercent = window.scrollY / (document.body.scrollHeight - window.innerHeight);
      setShowNudge(scrollPercent > 0.35);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [nudgeDismissed]);
  const navigate = useNavigate();

  // If user is already logged in, redirect to dashboard
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const result = await login(email, password);
      if (result?.requires_2fa) {
        setNeeds2FA(true);
        setPre2faToken(result.pre_2fa_token);
        setTotpCode("");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      if (detail) {
        setError(detail);
      } else if (err.message?.includes("Network Error")) {
        setError("Network error — please refresh the page and try again");
      } else {
        setError(`Login failed (${status || 'unknown'}). Please refresh the page.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      await verify2FA(pre2faToken, totpCode);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid 2FA code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      await register(email, password, name);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: Heart,
      title: "Daily Stress Check-Ins",
      description: "Quick mood and stress-level check-ins that take less than 60 seconds — build self-awareness daily"
    },
    {
      icon: Brain,
      title: "AI Stress Management Coaches",
      description: "Talk to AI-powered wellness coaches anytime you feel overwhelmed, anxious, or stuck"
    },
    {
      icon: Sparkles,
      title: "Guided Micro-Practices",
      description: "Bite-sized breathing, grounding, and calming exercises designed for busy, real-life moments"
    },
    {
      icon: Users,
      title: "Global Support Community",
      description: "You are not alone — connect with others who understand the daily grind of managing stress"
    },
    {
      icon: Award,
      title: "Wellness Streak Rewards",
      description: "Earn SMAHOO Coins for showing up daily — because consistency is the real superpower"
    },
    {
      icon: Play,
      title: "Healing Media & Podcasts",
      description: "On-demand wellness videos, Spotify playlists, and laughter therapy content for instant relief"
    }
  ];

  return (
    <div className="min-h-screen bg-spa-bone">
      {/* Navigation — outside hero overflow to prevent dropdown clipping */}
      <nav className="sticky top-0 z-50 flex items-center justify-between px-6 py-4 md:px-12 lg:px-20 bg-spa-bone/90 backdrop-blur-md border-b border-spa-border/30">
          <div className="flex items-center">
            <a href="/">
              <img src="/smahoo-logo.jpg" alt="SMAHOO ME.WELL Mind-SPA exPE" className="h-14 md:h-16 w-auto" />
            </a>
          </div>
          <div className="hidden md:flex items-center gap-6 text-spa-text-secondary text-sm">
            <a href="/about" className="hover:text-spa-moss transition-colors">About Us</a>
            <a href="/glossary" className="hover:text-spa-moss transition-colors">Glossary</a>
            <a href="#auth" className="hover:text-spa-moss transition-colors font-medium" onClick={() => { setAuthTab("login"); }}>Login</a>
            <a href="#auth" className="spa-button-primary px-6 py-2" onClick={() => { setAuthTab("register"); }}>Sign Up</a>
          </div>
          <button 
            className="md:hidden p-2 hover:bg-spa-muted rounded-xl"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="landing-mobile-menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6 text-spa-moss" /> : <Menu className="w-6 h-6 text-spa-moss" />}
          </button>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="relative z-10 md:hidden bg-white/95 backdrop-blur-xl border-b border-spa-border mx-4 rounded-2xl p-4 space-y-1 animate-fade-in">
            <a href="/about" className="block px-4 py-2.5 text-spa-text-secondary hover:text-spa-moss hover:bg-spa-muted rounded-xl transition-colors text-sm" onClick={() => setMobileMenuOpen(false)}>About Us</a>
            <a href="/glossary" className="block px-4 py-2.5 text-spa-text-secondary hover:text-spa-moss hover:bg-spa-muted rounded-xl transition-colors text-sm" onClick={() => setMobileMenuOpen(false)}>Glossary</a>
            <a href="#auth" className="block px-4 py-2.5 text-spa-text-secondary hover:text-spa-moss hover:bg-spa-muted rounded-xl transition-colors text-sm font-medium" onClick={() => { setMobileMenuOpen(false); setAuthTab("login"); }}>Login</a>
            <div className="pt-2 px-4">
              <a href="#auth" className="spa-button-primary w-full text-center block py-2.5 text-sm" onClick={() => { setMobileMenuOpen(false); setAuthTab("register"); }}>Sign Up</a>
            </div>
          </div>
        )}

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1615134732800-ca7ef7a3388c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxuYXR1cmUlMjBsYW5kc2NhcGUlMjBjYWxtfGVufDB8fHx8MTc3NTA4ODg3MHww&ixlib=rb-4.1.0&q=85')`
          }}
        >
          <div className="absolute inset-0 bg-spa-bone/85"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 px-6 py-16 md:px-12 lg:px-20 md:py-24 lg:py-32 max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <p className="overline-text mb-4">Your Daily Stress Management Companion</p>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-spa-moss leading-tight mb-6">
                Stressed, Overwhelmed, Depressed or Anxious?
              </h1>
              <p className="text-lg text-spa-text-secondary leading-relaxed mb-8 max-w-xl">
                Join today for daily Self-Care Hygiene. The <a href="/glossary#term-smahoo" className="text-spa-terracotta hover:underline">SMAHOO</a> <a href="/glossary#term-me-well" className="text-spa-terracotta hover:underline">ME.WELL</a> <a href="/glossary#term-mind-spa" className="text-spa-terracotta hover:underline">Mind-SPA</a> is your everyday companion for managing stress, checking in with yourself, and building emotional resilience — one small practice at a time.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#auth" className="spa-button-primary flex items-center gap-2">
                  Join Now — It Takes 60 Seconds <ArrowRight className="w-4 h-4" />
                </a>
                <a href="#features" className="spa-button-secondary flex items-center gap-2">
                  See How It Works
                </a>
              </div>
              <div className="flex items-center gap-4 mt-6 text-sm text-spa-text-secondary">
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-spa-sage" /> Basic profile included</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-spa-sage" /> No credit card required</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-spa-sage" /> Daily self-care check-in</span>
              </div>
            </div>

            {/* Auth Card */}
            <div id="auth" className="spa-card p-8 lg:p-10 max-w-md lg:ml-auto w-full" data-testid="auth-card">
              <h2 className="font-serif text-2xl font-medium text-spa-moss mb-2 text-center">
                Start Your Daily Self-Care Now
              </h2>
              <p className="text-sm text-spa-text-secondary text-center mb-6">Join with a Basic profile — AI coaching, mood tracking & community</p>
              
              <Tabs value={authTab} onValueChange={setAuthTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 bg-spa-muted rounded-full p-1 mb-6">
                  <TabsTrigger 
                    value="login" 
                    className="rounded-full data-[state=active]:bg-white data-[state=active]:text-spa-moss"
                    data-testid="login-tab"
                  >
                    Sign In
                  </TabsTrigger>
                  <TabsTrigger 
                    value="register"
                    className="rounded-full data-[state=active]:bg-white data-[state=active]:text-spa-moss"
                    data-testid="register-tab"
                  >
                    Register
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  {needs2FA ? (
                    <form onSubmit={handle2FAVerify} className="space-y-4">
                      <div className="text-center mb-2">
                        <div className="w-12 h-12 bg-spa-terracotta/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                          <Shield className="w-6 h-6 text-spa-terracotta" />
                        </div>
                        <h3 className="font-serif text-lg text-spa-moss">Two-Factor Authentication</h3>
                        <p className="text-sm text-spa-text-secondary mt-1">Enter the 6-digit code from your authenticator app</p>
                      </div>
                      <Input
                        type="text"
                        placeholder="000000"
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="spa-input text-center text-2xl tracking-[0.5em] font-mono"
                        required
                        maxLength={6}
                        autoFocus
                        data-testid="2fa-code-input"
                      />
                      {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                      <Button 
                        type="submit" 
                        className="w-full spa-button-primary"
                        disabled={isLoading || totpCode.length !== 6}
                        data-testid="2fa-submit"
                      >
                        {isLoading ? "Verifying..." : "Verify Code"}
                      </Button>
                      <button
                        type="button"
                        onClick={() => { setNeeds2FA(false); setPre2faToken(""); setTotpCode(""); setError(""); }}
                        className="w-full text-sm text-spa-text-secondary hover:text-spa-moss transition-colors"
                        data-testid="2fa-back-btn"
                      >
                        Back to login
                      </button>
                    </form>
                  ) : (
                  <form onSubmit={handleLogin} className="space-y-4">
                    <Input
                      type="email"
                      placeholder="Email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="spa-input"
                      required
                      data-testid="login-email"
                    />
                    <Input
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="spa-input"
                      required
                      data-testid="login-password"
                    />
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <Button 
                      type="submit" 
                      className="w-full spa-button-primary"
                      disabled={isLoading}
                      data-testid="login-submit"
                    >
                      {isLoading ? "Signing in..." : "Sign In"}
                    </Button>
                  </form>
                  )}
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <Input
                      type="text"
                      placeholder="Full name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="spa-input"
                      required
                      data-testid="register-name"
                    />
                    <Input
                      type="email"
                      placeholder="Email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="spa-input"
                      required
                      data-testid="register-email"
                    />
                    <Input
                      type="password"
                      placeholder="Create password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="spa-input"
                      required
                      minLength={6}
                      data-testid="register-password"
                    />
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <Button 
                      type="submit" 
                      className="w-full spa-button-primary"
                      disabled={isLoading}
                      data-testid="register-submit"
                    >
                      {isLoading ? "Creating account..." : "Create Account"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </div>

      {/* WHO Mental Health Action Plan 2013–2030 Section */}
      <section className="px-6 py-16 md:px-12 lg:px-20 bg-gradient-to-b from-spa-muted to-spa-bone" data-testid="who-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-8 items-start">
            {/* Countdown */}
            <div className="lg:col-span-2">
              <div className="spa-card bg-gradient-to-br from-spa-moss to-spa-moss/90 text-white text-center sticky top-24">
                <p className="text-white/70 text-xs font-medium tracking-widest uppercase mb-3">WHO Action Plan Countdown</p>
                <p className="font-serif text-5xl sm:text-6xl font-light mb-1">
                  {(() => {
                    const now = new Date();
                    const end = new Date('2030-12-31T23:59:59');
                    const diff = end - now;
                    const days = Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
                    const years = Math.floor(days / 365);
                    const remainingDays = days % 365;
                    return `${years}y ${remainingDays}d`;
                  })()}
                </p>
                <p className="text-white/60 text-sm mb-5">until 2030 deadline</p>
                <div className="h-px bg-white/20 mb-5" />
                <p className="text-white/80 text-sm leading-relaxed">
                  The WHO Mental Health Action Plan (2013–2030) calls on all nations to act. <strong className="text-white">This SuperAPP answers that call.</strong>
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-3 space-y-6">
              <div>
                <p className="overline-text mb-3">Why This SuperAPP Exists</p>
                <h2 className="font-serif text-2xl sm:text-3xl font-medium text-spa-moss mb-4 leading-tight">
                  Closing the Gap Between Global Policy & Everyday People
                </h2>
                <p className="text-spa-text-secondary leading-relaxed mb-4">
                  The SuperAPP was developed to help close an awareness and engagement gap between international mental health policy and the people who ultimately experience health and community services.
                </p>
              </div>

              <div className="spa-card border-l-4 border-l-spa-terracotta bg-spa-terracotta/5">
                <p className="text-xs font-medium text-spa-terracotta tracking-wider uppercase mb-2">Connected to WHO Objective 1</p>
                <p className="font-serif text-lg text-spa-moss italic leading-relaxed">
                  &ldquo;Strengthen effective leadership and governance for mental health.&rdquo;
                </p>
                <p className="text-sm text-spa-text-secondary mt-2">
                  WHO calls upon Member States to engage stakeholders from relevant sectors in the development and implementation of mental health policies, laws and services.
                </p>
              </div>

              <div className="flex items-center gap-3 mt-2">
                <a
                  href="https://www.who.int/initiatives/mental-health-action-plan-2013-2030"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-spa-moss/20 rounded-xl text-sm font-medium text-spa-moss hover:bg-spa-moss/5 transition-colors"
                  data-testid="who-learn-more-landing"
                >
                  Learn More <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-12 md:px-12 lg:px-20 bg-spa-moss text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="mb-4">
                <img src="/smahoo-logo.jpg" alt="SMAHOO ME.WELL Mind-SPA exPE" className="h-16 w-auto rounded-lg bg-white p-1" />
              </div>
              <p className="text-white/70 text-sm">
                SMAHOO ME.WELL Mind-SPA SuperAPP - Your gateway to holistic mental and emotional wellness.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-4">Ecosystem</h4>
              <ul className="space-y-2 text-white/70 text-sm">
                <li><a href="https://www.mind-spa.icu" target="_blank" rel="noopener noreferrer" className="hover:text-white">mind-spa.icu</a></li>
                <li><a href="https://www.smahoocoins.online" target="_blank" rel="noopener noreferrer" className="hover:text-white">smahoocoins.online</a></li>
                <li><a href="https://www.smahoo.art" target="_blank" rel="noopener noreferrer" className="hover:text-white">smahoo.art</a></li>
                <li><a href="https://www.globalvillage.info" target="_blank" rel="noopener noreferrer" className="hover:text-white">globalvillage.info</a></li>
                <li><a href="https://www.gift-basket.store" target="_blank" rel="noopener noreferrer" className="hover:text-white">gift-basket.store</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4">Resources</h4>
              <ul className="space-y-2 text-white/70 text-sm">
                <li><a href="/glossary" className="hover:text-white">Glossary of Terms</a></li>
                <li><a href="https://www.riceandpeasforthesoul.tv" target="_blank" rel="noopener noreferrer" className="hover:text-white">Rice And Peas TV</a></li>
                <li><a href="https://www.smaho-arts.org" target="_blank" rel="noopener noreferrer" className="hover:text-white">SMAHo.ARTS</a></li>
                <li><a href="https://www.shirvington.ca" target="_blank" rel="noopener noreferrer" className="hover:text-white">Shirvington.ca</a></li>
                <li><a href="https://www.gift-basket.store" target="_blank" rel="noopener noreferrer" className="hover:text-white">Gift Basket Store</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-4">Contact</h4>
              <ul className="space-y-2 text-white/70 text-sm">
                <li>info@smaho-arts.org</li>
                <li>Edmonton, Alberta, Canada</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 text-center text-white/50 text-sm">
            <p>&copy; {new Date().getFullYear()} SMAHOO ME.WELL Mind-SPA SuperAPP. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Floating Nudge Bar */}
      {showNudge && !nudgeDismissed && (
        <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up" data-testid="floating-nudge">
          <div className="bg-white/95 backdrop-blur-xl border-t border-spa-border shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="hidden sm:flex w-9 h-9 bg-amber-100 rounded-xl items-center justify-center flex-shrink-0">
                  <Award className="w-5 h-5 text-amber-600" />
                </div>
                <p className="text-spa-moss text-sm truncate">
                  <span className="font-medium">Stressed or overwhelmed?</span>
                  <span className="hidden sm:inline text-spa-text-secondary"> Join now — daily self-care hygiene takes just 60 seconds</span>
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <a href="#auth" className="px-5 py-2 bg-spa-terracotta text-white rounded-full text-sm font-medium hover:bg-spa-terracotta/90 transition-colors whitespace-nowrap" data-testid="nudge-cta">
                  Join Now
                </a>
                <button onClick={() => setNudgeDismissed(true)} className="p-1.5 text-spa-text-secondary hover:text-spa-moss rounded-lg hover:bg-spa-muted transition-colors" data-testid="nudge-dismiss">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
